import { useState, type ClipboardEvent, type SVGProps } from "react";
import { useIntl } from "react-intl";
import { Input, type FormFieldControlProps } from "@lendcord/concord";

function EyeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a17.4 17.4 0 0 1-3.22 4.34M6.61 6.61A17.5 17.5 0 0 0 1 12s4 8 11 8a9.26 9.26 0 0 0 5.39-1.61M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

type PasswordInputProps = FormFieldControlProps & {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  disablePaste?: boolean;
};

// FormField clones its single child and injects id/aria/state props onto it —
// this wrapper forwards those onto the real <input> so the show/hide button
// doesn't break the label[for] <-> input[id] association.
export function PasswordInput({
  value,
  onChange,
  placeholder,
  autoComplete,
  disablePaste,
  ...controlProps
}: PasswordInputProps) {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    if (disablePaste) {
      event.preventDefault();
    }
  }

  return (
    <div className="relative">
      <Input
        {...controlProps}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onPaste={handlePaste}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="pr-10"
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((current) => !current)}
        aria-label={intl.formatMessage({
          id: visible ? "auth.passwordInput.hidePasswordLabel" : "auth.passwordInput.showPasswordLabel",
        })}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-text-subtle hover:text-text"
      >
        {visible ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      </button>
    </div>
  );
}
